import type {
  ChangePushCategoryUserSettingRequest,
  ClientPushCategoryUserSetting,
  ClientPushCategoryUserSettingChangeResponse
} from '@DigitaleDoerfer/digitale-doerfer-api';
import { useQueryClient } from '@tanstack/react-query';

import { useSharedPushApi } from '@/features/api/hooks/shared-push/useSharedPushApi';
import { useApiMutation } from '@/features/api/hooks/useApiMutation';
import { getPushCategoriesQueryKey } from '@/features/profile-setting/utils/notificationSettingCache';

export const useChangePushCategorySetting = () => {
  const queryClient = useQueryClient();
  const api = useSharedPushApi();

  return useApiMutation<
    ClientPushCategoryUserSettingChangeResponse,
    unknown,
    ChangePushCategoryUserSettingRequest
  >({
    mutationFn: (
      changePushCategoryUserSettingRequest: ChangePushCategoryUserSettingRequest
    ) =>
      api.changePushCategoryUserSetting(changePushCategoryUserSettingRequest),
    onSuccess: (data, variables) => {
      queryClient.setQueryData<ClientPushCategoryUserSetting[]>(
        getPushCategoriesQueryKey(),
        (settings) =>
          settings?.map((setting) => {
            const changedSetting = [
              data.changedPushCategoryUserSetting,
              ...data.changedDependentPushCategoryUserSettings
            ].find(({ id }) => id === setting.id);

            if (!changedSetting) {
              return setting;
            }

            if (
              setting.id === variables.categoryId &&
              setting.loudPushEnabled !== variables.loudPushEnabled
            ) {
              return setting;
            }

            return changedSetting;
          })
      );
    },
    onError: (_error, variables) => {
      const queryKey = getPushCategoriesQueryKey();
      const currentSettings =
        queryClient.getQueryData<ClientPushCategoryUserSetting[]>(queryKey);
      const failedSetting = currentSettings?.find(
        ({ id }) => id === variables.categoryId
      );

      if (failedSetting?.loudPushEnabled === variables.loudPushEnabled) {
        queryClient.invalidateQueries({ queryKey });
      }
    }
  });
};
